import { useEffect, useMemo } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { PAGE_NAVIGATION_KEYS } from "@/lib/pageNavigationKeys"
import { writePersistedNavigationItems } from "@/hooks/usePersistedPageNavigation"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import { useConversationsNavigation } from "../contexts/ConversationsNavigationContext"
import { getConversationsSimulationOutputIds } from "../utils/conversationsTableState"

/**
 * Keeps the shared conversations-navigation context and its persisted session storage
 * in sync with whichever conversations are currently loaded in the table, so j/k paging
 * into a conversation's detail view and back restores the right position.
 */
export function useConversationsNavigationDataSync(
  allConversationsData: ConversationsTableResult[],
) {
  const { setNavigationData } = useConversationsNavigation()
  const conversationsStorageKey = useScopedStorageKey(
    PAGE_NAVIGATION_KEYS.conversations,
  )
  const simulationOutputIds = useMemo(
    () => getConversationsSimulationOutputIds(allConversationsData),
    [allConversationsData],
  )

  useEffect(() => {
    if (simulationOutputIds.length > 0) {
      setNavigationData(simulationOutputIds, "")
      writePersistedNavigationItems(
        conversationsStorageKey,
        simulationOutputIds.map((id) => ({ id })),
      )
    }
  }, [simulationOutputIds, setNavigationData, conversationsStorageKey])
}
